import React, { useState } from 'react';
import { FaChevronLeft, FaChevronDown, FaPlay, FaCalculator, FaBrain, FaPenFancy, FaBook, FaCheckCircle } from 'react-icons/fa';
import { FaThumbsUp } from "react-icons/fa";

const subjectConfig = {
  'Maths': {
    icon: <FaCalculator className="text-xl" />,
    color: "from-blue-500 to-teal-400",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    key: 'Maths'
  },
  'ThinkingSkills': {
    icon: <FaBrain className="text-xl" />,
    color: "from-purple-500 to-pink-400",
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    key: 'ThinkingSkills'
  },
  'Writing': {
    icon: <FaPenFancy className="text-xl" />,
    color: "from-orange-500 to-yellow-400",
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
    key: 'Writing'
  },
  'Reading': {
    icon: <FaBook className="text-xl" />,
    color: "from-teal-500 to-green-400",
    bgColor: "bg-teal-50",
    textColor: "text-teal-700",
    key: 'Reading'
  }
};

const MockTestStartPage = ({ test, onBack, onStartTest, testProgress = {} }) => {
  const [selectedSubject, setSelectedSubject] = useState('');

  // Extract test number from test ID with safe fallback
  const getTestNumber = () => {
    if (!test || !test.id) return 1;

    try {
      const parts = test.id.split('-');
      if (parts.length >= 3) {
        return parseInt(parts[2]) || 1;
      }
      return 1;
    } catch (error) {
      return 1;
    }
  };

  const testNumber = getTestNumber();

  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
  };

  const handleStartTest = () => {
    if (selectedSubject) {
      onStartTest(test.id, selectedSubject, true);
    } else {
      alert('Please select a subject to start the test');
    }
  };

  // Check if a subject is completed with safe fallbacks
  const isSubjectCompleted = (subjectDisplayName) => {
    // try {
    //   const subjectKey = subjectConfig[subjectDisplayName]?.key;
    //   if (!subjectKey || !testProgress[testNumber]) return false;

    //   return testProgress[testNumber]?.completedSubjects?.has(subjectKey) || false;
    // } catch (error) {
    //   return false;
    // }
    return false;
  };

  // Function to get display name for dropdown
  const getSubjectDisplayName = (subjectKey) => {
    const displayNames = {
      'Maths': 'Mathematical Reasoning Test',
      'ThinkingSkills': 'Thinking Skills Test',
      'Reading': 'Reading Test',
      'Writing': 'Writing Test'
    };
    return displayNames[subjectKey] || subjectKey;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Back Button */}
          <button
            onClick={onBack}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors mb-6"
          >
            <FaChevronLeft className="mr-2" /> Back to Mock Tests
          </button>

          {/* Welcome Message */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              All 4 tests - {test?.name || 'Mock Test'}
            </h1>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-2xl mx-auto">
              <p className="text-yellow-800 font-medium flex items-center justify-center text-center gap-2 w-full">
                <FaThumbsUp className="text-yellow-600" />
                <strong>Tip:</strong> Complete all 4 subjects to unlock the next mock test.
              </p>
            </div>
          </div>

          {/* Subject Selection */}
          <div className="max-w-md mx-auto">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Subject to Start:
            </label>
            <div className="relative">
              <select
                value={selectedSubject}
                onChange={(e) => handleSubjectChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
              >
                <option value="">Choose a subject...</option>
                {Object.entries(subjectConfig).map(([subject, config]) => (
                  <option key={subject} value={subject}>
                    {getSubjectDisplayName(subject)}
                  </option>
                ))}
              </select>
            </div>

            {selectedSubject && (
              <div className={`mt-4 p-4 rounded-lg border ${subjectConfig[selectedSubject]?.bgColor || 'bg-gray-50'} border-gray-200`}>
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full bg-gradient-to-br ${subjectConfig[selectedSubject]?.color || 'from-gray-500 to-gray-400'} text-white`}>
                    {subjectConfig[selectedSubject]?.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">{getSubjectDisplayName(selectedSubject)}</h3>
                    <p className="text-sm text-gray-600">
                      {selectedSubject === 'Reading' ? '45 minutes' : '40 minutes'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Start Button */}
            <button
              onClick={handleStartTest}
              disabled={!selectedSubject || isSubjectCompleted(selectedSubject)}
              className={`w-full mt-6 px-6 py-3 rounded-lg font-semibold text-white transition-all flex items-center justify-center space-x-2 ${selectedSubject && !isSubjectCompleted(selectedSubject)
                  ? `start-test-button`
                  : 'bg-gray-400 cursor-not-allowed'
                }`}
            >
              <FaPlay className="text-sm" />
              <span>Continue to Instructions</span>
            </button>

            {/* Completion Message - Show below the start button */}
            {selectedSubject && isSubjectCompleted(selectedSubject) && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2 text-green-700">
                  <FaCheckCircle className="text-green-500" />
                  <span className="font-medium">Subject Completed!</span>
                </div>
                <p className="text-sm text-green-600 mt-1">
                  Great job! You've already completed this subject.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockTestStartPage;